<template>
  <DxPopup
    :visible="visible"
    :title="$t('salary_calculator.work_log.overlap_cleanup.title')"
    :width="420"
    height="auto"
    :drag-enabled="false"
    :close-on-outside-click="!loading"
    @hiding="close"
  >
    <div class="overlap-cleanup-content">
      <div>
        <label class="control-label">{{ $t('common.employee_name') }}</label>
        <DxSelectBox
          :data-source="employees || []"
          :value.sync="employeeUuid"
          value-expr="uuid"
          display-expr="name"
          :search-enabled="true"
          :show-clear-button="true"
          placeholder="-"
        />
      </div>
      <div>
        <label class="control-label">{{ $t('common.date_from') }}</label>
        <DxDateBox
          :value.sync="dateFrom"
          type="date"
          display-format="yyyy-MM-dd"
          date-serialization-format="yyyy-MM-dd"
          :max="dateTo || undefined"
          @value-changed="onDateFromChanged"
        />
      </div>
      <div>
        <label class="control-label">{{ $t('common.date_to') }}</label>
        <DxDateBox
          :value.sync="dateTo"
          type="date"
          display-format="yyyy-MM-dd"
          date-serialization-format="yyyy-MM-dd"
          :min="dateFrom || undefined"
          :max="maxDateTo || undefined"
        />
      </div>
      <small class="text-muted">
        {{ $t('salary_calculator.work_log.overlap_cleanup.max_interval', { months: maxMonths }) }}
      </small>
      <DxButton
        :text="$t('salary_calculator.work_log.overlap_cleanup.clean_up')"
        type="danger"
        :disabled="!canSubmit"
        @click="onCleanup"
      />
    </div>
  </DxPopup>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { DxPopup } from 'devextreme-vue/popup';
import DxSelectBox from 'devextreme-vue/select-box';
import DxDateBox from 'devextreme-vue/date-box';
import DxButton from 'devextreme-vue/button';
import { confirmDialog, successMessage } from '@/helpers/common';

const DATE_FORMAT = 'YYYY-MM-DD';
const MAX_MONTHS = 4;

export default {
  name: 'SCWorkLogOverlapCleanupDialog',
  components: { DxPopup, DxSelectBox, DxDateBox, DxButton },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      employeeUuid: null,
      dateFrom: null,
      dateTo: null,
      loading: false,
      maxMonths: MAX_MONTHS,
    };
  },
  computed: {
    ...mapGetters({
      employees: 'scEmployee/scEmployees',
    }),
    maxDateTo() {
      return this.dateFrom ? moment(this.dateFrom, DATE_FORMAT).add(MAX_MONTHS, 'months').format(DATE_FORMAT) : null;
    },
    canSubmit() {
      return !this.loading && !!this.dateFrom && !!this.dateTo
        && this.dateTo >= this.dateFrom && this.dateTo <= this.maxDateTo;
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.employeeUuid = null;
        this.dateTo = moment().format(DATE_FORMAT);
        this.dateFrom = moment().subtract(MAX_MONTHS, 'months').format(DATE_FORMAT);
      }
    },
  },
  methods: {
    ...mapActions({
      cleanupOverlaps: 'scWorkLog/cleanupSCWorkLogOverlaps',
    }),
    close() {
      if (!this.loading) {
        this.$emit('update:visible', false);
      }
    },
    onDateFromChanged() {
      if (this.dateTo && this.maxDateTo && this.dateTo > this.maxDateTo) {
        this.dateTo = this.maxDateTo;
      }
    },
    onCleanup() {
      if (!this.canSubmit) {
        return;
      }
      confirmDialog(
        this.$t('salary_calculator.work_log.overlap_cleanup.confirm'),
        this.$t('warning'),
        {
          okText: this.$t('common.continue'),
          cancelText: this.$t('common.cancel'),
        }
      ).then(() => {
        this.loading = true;
        return this.cleanupOverlaps({
          employee_uuid: this.employeeUuid,
          date_from: this.dateFrom,
          date_to: this.dateTo,
        }).then((resp) => {
          const result = resp?.data || {};
          successMessage(this, this.$t('salary_calculator.work_log.overlap_cleanup.success', {
            work_logs: result.work_logs?.deleted ?? 0,
            links: result.work_collectors?.deleted_links ?? 0,
            collectors: result.work_collectors?.deleted_collectors ?? 0,
          }));
          this.$emit('cleaned', result);
          this.loading = false;
          this.close();
        });
      }).catch(() => {
        this.loading = false;
      });
    },
  },
};
</script>

<style scoped>
.overlap-cleanup-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px;
}
</style>
